/**
 * Opportunity Portfolio Component
 * Shows ranked opportunities with CAPEX stratification, MACC curves, tCO2e reduction, AI Report, and PDF Export
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  BarChart3, Loader2, RefreshCw, Download, TrendingUp, 
  DollarSign, Clock, Leaf, Award, ArrowLeft, Info, Filter,
  FileText, Sparkles, X, CheckCircle, AlertTriangle
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, ReferenceLine
} from 'recharts';
import {
  generateBrandedPdf,
  fetchTenantLogoDataUrl,
  PLATFORM_ATTRIBUTION,
  AI_DISCLAIMER,
} from '../../utils/pdfBranding';

const API = process.env.REACT_APP_BACKEND_URL;

// Grid emission factor (India CEA Baseline) - kgCO2 per kWh
const EMISSION_FACTOR = 0.716;

// CAPEX tier thresholds (INR)
const CAPEX_TIERS = {
  'No CAPEX': { min: 0, max: 10000, color: '#22c55e', bgColor: 'bg-green-50', borderColor: 'border-green-200', textColor: 'text-green-800' },
  'Low CAPEX': { min: 10000, max: 500000, color: '#3b82f6', bgColor: 'bg-blue-50', borderColor: 'border-blue-200', textColor: 'text-blue-800' },
  'Medium CAPEX': { min: 500000, max: 2500000, color: '#f59e0b', bgColor: 'bg-amber-50', borderColor: 'border-amber-200', textColor: 'text-amber-800' },
  'High CAPEX': { min: 2500000, max: Infinity, color: '#ef4444', bgColor: 'bg-red-50', borderColor: 'border-red-200', textColor: 'text-red-800' },
};

const getCapexTier = (capexAvg) => {
  if (capexAvg <= 10000) return 'No CAPEX';
  if (capexAvg <= 500000) return 'Low CAPEX';
  if (capexAvg <= 2500000) return 'Medium CAPEX';
  return 'High CAPEX';
};

const OpportunityPortfolio = ({ study, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [portfolioData, setPortfolioData] = useState(null);
  const [activeCapexFilter, setActiveCapexFilter] = useState('All');
  const [seuData, setSeuData] = useState([]);
  
  // AI Report state
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const [aiReport, setAiReport] = useState(null);
  const [showAiReport, setShowAiReport] = useState(false);
  
  // PDF state
  const [pdfLoading, setPdfLoading] = useState(false);
  
  const chartRef = useRef(null);

  useEffect(() => {
    if (study?.id) {
      fetchPortfolioData();
      fetchSeuData();
    }
  }, [study?.id]);

  const fetchPortfolioData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(
        `${API}/api/opportunity-v2/matching/study/${study.id}/calculate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add tCO2e calculations and CAPEX tier to each opportunity
      const calculationsWithExtras = res.data.calculations?.map(calc => {
        const energySavingsKwh = calc.estimated_annual_savings_inr / 8;
        const tco2eReduction = (energySavingsKwh / 1000) * EMISSION_FACTOR;
        const avgCapex = (calc.estimated_capex_low + calc.estimated_capex_high) / 2;
        const lifetimeTco2e = tco2eReduction * (calc.lifecycle_years || 10);
        const lifetimeSavings = calc.estimated_annual_savings_inr * (calc.lifecycle_years || 10);
        const netCost = avgCapex - lifetimeSavings;
        const abatementCost = lifetimeTco2e > 0 ? netCost / lifetimeTco2e : 0;
        
        return {
          ...calc,
          energy_savings_kwh: Math.round(energySavingsKwh),
          tco2e_reduction_annual: Math.round(tco2eReduction * 100) / 100,
          tco2e_reduction_lifetime: Math.round(lifetimeTco2e * 100) / 100,
          abatement_cost: Math.round(abatementCost),
          avg_capex: avgCapex,
          capex_tier: getCapexTier(avgCapex),
        };
      }) || [];
      
      const totalTco2eAnnual = calculationsWithExtras.reduce((sum, c) => sum + (c.tco2e_reduction_annual || 0), 0);
      const totalTco2eLifetime = calculationsWithExtras.reduce((sum, c) => sum + (c.tco2e_reduction_lifetime || 0), 0);
      
      const tierCounts = { 'No CAPEX': 0, 'Low CAPEX': 0, 'Medium CAPEX': 0, 'High CAPEX': 0 };
      calculationsWithExtras.forEach(c => {
        tierCounts[c.capex_tier]++;
      });
      
      setPortfolioData({
        ...res.data,
        calculations: calculationsWithExtras,
        summary: {
          ...res.data.summary,
          total_tco2e_annual: Math.round(totalTco2eAnnual * 10) / 10,
          total_tco2e_lifetime: Math.round(totalTco2eLifetime),
          tier_counts: tierCounts,
        }
      });
      
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSeuData = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(
        `${API}/api/opportunity-v2/matching/study/${study.id}/seu-data`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSeuData(res.data.seus || []);
    } catch (error) {
      console.error('Error fetching SEU data:', error);
    }
  };

  // Filter calculations by CAPEX tier
  const filteredCalculations = useMemo(() => {
    if (!portfolioData?.calculations) return [];
    if (activeCapexFilter === 'All') return portfolioData.calculations;
    return portfolioData.calculations.filter(c => c.capex_tier === activeCapexFilter);
  }, [portfolioData, activeCapexFilter]);

  // MACC data
  const maccData = useMemo(() => {
    if (!filteredCalculations.length) return [];
    
    const sorted = [...filteredCalculations]
      .filter(c => c.tco2e_reduction_lifetime > 0)
      .sort((a, b) => a.abatement_cost - b.abatement_cost);
    
    let cumulativeWidth = 0;
    return sorted.map((calc, idx) => {
      const startX = cumulativeWidth;
      cumulativeWidth += calc.tco2e_reduction_lifetime;
      return {
        name: calc.capex_item_name?.substring(0, 20) || `Opp ${idx + 1}`,
        fullName: `${calc.capex_item_name} (${calc.equipment_type || 'N/A'})`,
        abatementCost: calc.abatement_cost,
        tco2e: calc.tco2e_reduction_lifetime,
        cumulativeWidth: cumulativeWidth,
        startX: startX,
        payback: calc.simple_payback_years,
        capexTier: calc.capex_tier,
        tierColor: CAPEX_TIERS[calc.capex_tier]?.color || '#6b7280',
      };
    });
  }, [filteredCalculations]);

  // Generate AI Report
  const generateAiReport = async () => {
    setAiReportLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(
        `${API}/api/opportunity-v2/matching/study/${study.id}/portfolio-ai-report`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setAiReport(res.data.report);
        setShowAiReport(true);
      } else {
        alert('Error generating AI report: ' + (res.data.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Error generating AI report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAiReportLoading(false);
    }
  };

  // Generate PDF Report — uses html2pdf.js for elegant rendering matching Final Report
  const generatePdfReport = async () => {
    setPdfLoading(true);
    const pdfStyleId = 'portfolio-pdf-style';

    try {
      const { default: html2canvas } = await import('html2canvas');
      const tenantLogoDataUrl = await fetchTenantLogoDataUrl();
      const sum = portfolioData?.summary || {};
      const calcs = portfolioData?.calculations || [];

      // Build tier summary data
      const tierSummaries = Object.entries(CAPEX_TIERS).map(([tier, config]) => {
        const tierOpps = calcs.filter(c => c.capex_tier === tier);
        return {
          tier, color: config.color,
          count: tierOpps.length,
          savings: tierOpps.reduce((s, c) => s + (c.estimated_annual_savings_inr || 0), 0),
          tco2e: tierOpps.reduce((s, c) => s + (c.tco2e_reduction_annual || 0), 0),
        };
      });

      // Quick wins & high-impact recommendations
      const quickWins = calcs
        .filter(c => ['No CAPEX', 'Low CAPEX'].includes(c.capex_tier) && c.simple_payback_years && c.simple_payback_years < 2)
        .slice(0, 5);
      const highImpact = calcs
        .filter(c => ['Medium CAPEX', 'High CAPEX'].includes(c.capex_tier))
        .sort((a, b) => (b.tco2e_reduction_annual || 0) - (a.tco2e_reduction_annual || 0))
        .slice(0, 5);

      // Capture MACC chart as image
      let chartImgSrc = '';
      if (chartRef.current) {
        try {
          const canvas = await html2canvas(chartRef.current, { scale: 2, backgroundColor: '#ffffff' });
          chartImgSrc = canvas.toDataURL('image/png');
        } catch (e) { console.warn('Chart capture failed:', e); }
      }

      // Build printable HTML
      const container = document.createElement('div');
      container.id = 'portfolio-pdf-content';
      container.innerHTML = `
        <style>
          #portfolio-pdf-content {
            font-family: Arial, Helvetica, sans-serif;
            color: #1f2937; font-size: 13px; line-height: 1.6;
            max-width: 800px; margin: 0 auto;
          }
          #portfolio-pdf-content h1 { font-size: 26px; color: #4338ca; margin-bottom: 6px; }
          #portfolio-pdf-content h2 { font-size: 18px; color: #4338ca; border-bottom: 2px solid #6366f1; padding-bottom: 5px; margin: 24px 0 12px; }
          #portfolio-pdf-content h3 { font-size: 15px; color: #374151; margin: 12px 0 8px; }
          #portfolio-pdf-content table { width: 100%; border-collapse: collapse; font-size: 11px; }
          #portfolio-pdf-content th { background: #f3f4f6; padding: 7px 8px; text-align: left; font-weight: 600; border: 1px solid #e5e7eb; }
          #portfolio-pdf-content td { padding: 6px 8px; border: 1px solid #e5e7eb; }
          #portfolio-pdf-content tr:nth-child(even) { background: #f9fafb; }
          #portfolio-pdf-content .top3 { background: #dcfce7 !important; }
          #portfolio-pdf-content .cover { text-align: center; padding: 60px 0 40px; min-height: 272mm; display: flex; flex-direction: column; justify-content: center; align-items: center; }
          #portfolio-pdf-content .cover .subtitle { color: #6b7280; font-size: 16px; }
          #portfolio-pdf-content .cover .date { color: #9ca3af; font-size: 13px; margin-top: 8px; }
          #portfolio-pdf-content .summary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 12px 0; }
          #portfolio-pdf-content .summary-item { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; }
          #portfolio-pdf-content .summary-item .label { font-size: 11px; color: #6b7280; }
          #portfolio-pdf-content .summary-item .value { font-size: 18px; font-weight: 700; color: #1f2937; }
          #portfolio-pdf-content .tier-bar { display: flex; align-items: center; gap: 8px; padding: 8px 0; border-bottom: 1px solid #f3f4f6; font-size: 13px; }
          #portfolio-pdf-content .tier-dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }
          #portfolio-pdf-content .chart-img { width: 100%; max-height: 280px; object-fit: contain; margin: 10px 0; }
          #portfolio-pdf-content .rec-item { padding: 6px 0 6px 12px; border-left: 3px solid #6366f1; margin: 6px 0; font-size: 12px; }
          #portfolio-pdf-content .footer { text-align: center; color: #9ca3af; font-size: 10px; margin-top: 30px; padding: 12px 0 20px; border-top: 1px solid #e5e7eb; }
          #portfolio-pdf-content .page-break { page-break-before: always; break-before: page; }
        </style>

        <!-- Cover Page (tenant branding) -->
        <div class="cover">
          ${tenantLogoDataUrl
            ? `<img src="${tenantLogoDataUrl}" alt="Company logo" style="max-height:80px;max-width:240px;object-fit:contain;display:inline-block;margin-bottom:24px;" />`
            : `<div style="display:inline-flex;align-items:center;justify-content:center;width:240px;height:80px;border:2px dashed #d1d5db;border-radius:8px;background:#f9fafb;margin-bottom:24px;"><span style="font-size:14px;font-style:italic;color:#9ca3af;">Your logo here</span></div>`}
          <h1>Opportunity Portfolio Report</h1>
          <p class="subtitle">${study?.name || 'Energy Study'}</p>
          <p class="date">Generated: ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>

        <!-- Executive Summary -->
        <div>
          <h2>1. Executive Summary</h2>
          <div class="summary-grid">
            <div class="summary-item"><div class="label">Total Opportunities</div><div class="value">${portfolioData?.total_opportunities || 0}</div></div>
            <div class="summary-item"><div class="label">Total CAPEX Range</div><div class="value">&#8377;${(sum.total_capex_low / 100000)?.toFixed(1) || 0}L &ndash; &#8377;${(sum.total_capex_high / 100000)?.toFixed(1) || 0}L</div></div>
            <div class="summary-item"><div class="label">Annual Savings Potential</div><div class="value">&#8377;${(sum.total_annual_savings / 100000)?.toFixed(1) || 0}L</div></div>
            <div class="summary-item"><div class="label">CO&#8322;e Reduction (Annual)</div><div class="value">${sum.total_tco2e_annual?.toFixed(1) || 0} tCO&#8322;e</div></div>
            <div class="summary-item"><div class="label">Average Payback</div><div class="value">${sum.avg_payback_years?.toFixed(1) || 'N/A'} years</div></div>
            <div class="summary-item"><div class="label">SEUs Analyzed</div><div class="value">${seuData.length}</div></div>
          </div>

          <!-- CAPEX Tier Summary -->
          <h2>2. CAPEX Tier Breakdown</h2>
          ${tierSummaries.map(t => `
            <div class="tier-bar">
              <span class="tier-dot" style="background:${t.color}"></span>
              <strong>${t.tier}</strong>: ${t.count} opportunities &bull; &#8377;${(t.savings / 100000).toFixed(1)}L savings/yr &bull; ${t.tco2e.toFixed(1)} tCO&#8322;e/yr
            </div>
          `).join('')}
        </div>

        <!-- SEU Table -->
        <div class="page-break">
          <h2>3. Significant Energy Uses (SEUs)</h2>
          <table>
            <thead><tr><th>Equipment</th><th>Type</th><th>Capacity</th><th>Load %</th><th>Annual Energy</th></tr></thead>
            <tbody>
              ${seuData.map(s => `
                <tr>
                  <td>${(s.equipment_name || s.equipment_type || 'N/A').substring(0, 30)}</td>
                  <td>${s.resource_type || '-'}</td>
                  <td>${s.rated_capacity || '-'} ${s.rated_capacity_unit || 'kW'}</td>
                  <td>${s.load_percent || '-'}%</td>
                  <td>${s.annual_energy ? (s.annual_energy / 1000).toFixed(0) + ' MWh' : '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <!-- Ranked Opportunities -->
        <div class="page-break">
          <h2>4. Ranked Opportunities</h2>
          <table>
            <thead><tr><th>#</th><th>Tier</th><th>Opportunity</th><th>Equipment</th><th>CAPEX</th><th>Savings/yr</th><th>Payback</th><th>tCO&#8322;e/yr</th></tr></thead>
            <tbody>
              ${calcs.map((c, i) => `
                <tr class="${i < 3 ? 'top3' : ''}">
                  <td>${c.rank}</td>
                  <td>${c.capex_tier}</td>
                  <td>${(c.capex_item_name || '-').substring(0, 28)}</td>
                  <td>${(c.equipment_type || '-').substring(0, 18)}</td>
                  <td>&#8377;${(c.avg_capex / 1000)?.toFixed(0) || 0}K</td>
                  <td>&#8377;${(c.estimated_annual_savings_inr / 100000)?.toFixed(2) || 0}L</td>
                  <td>${c.simple_payback_years ? c.simple_payback_years + ' yr' : '-'}</td>
                  <td>${c.tco2e_reduction_annual?.toFixed(1) || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <!-- MACC Chart -->
        <div class="page-break">
          <h2>5. Marginal Abatement Cost Curve (MACC)</h2>
          ${chartImgSrc
            ? `<img class="chart-img" src="${chartImgSrc}" alt="MACC Chart" />`
            : '<p style="color:#9ca3af;text-align:center;padding:40px 0;">MACC chart not available</p>'}
          <p style="font-size:11px;color:#6b7280;margin-top:6px;">
            Each bar = one opportunity. Height = cost per tCO&#8322;e reduced. Width = tCO&#8322;e potential. Bars below zero = net saving.
          </p>
        </div>

        <!-- Recommendations -->
        <div class="page-break">
          <h2>6. Recommendations</h2>
          <h3>Quick Wins (No/Low CAPEX, &lt; 2yr payback)</h3>
          ${quickWins.length > 0
            ? quickWins.map(c => `<div class="rec-item">${c.capex_item_name} &mdash; &#8377;${(c.estimated_annual_savings_inr / 100000).toFixed(2)}L savings, ${c.simple_payback_years}yr payback</div>`).join('')
            : '<p style="color:#9ca3af;">No opportunities in this category</p>'}

          <h3>High Impact (Medium/High CAPEX)</h3>
          ${highImpact.length > 0
            ? highImpact.map(c => `<div class="rec-item">${c.capex_item_name} &mdash; ${c.tco2e_reduction_annual?.toFixed(1)} tCO&#8322;e/yr reduction</div>`).join('')
            : '<p style="color:#9ca3af;">No opportunities in this category</p>'}
        </div>

        <div class="footer" style="text-align:center;color:#6b7280;font-size:10px;margin-top:30px;padding:14px 0 16px;border-top:1px solid #e5e7eb;page-break-inside:avoid;">
          <p style="margin:0 0 4px 0;">${PLATFORM_ATTRIBUTION}</p>
          <p style="margin:0;font-style:italic;color:#9ca3af;">${AI_DISCLAIMER}</p>
        </div>
      `;

      // Render off-screen for layout calculation only — generateBrandedPdf
      // clones it again into its own wrapper for capture, so we just need it
      // attached briefly for image/font load.
      container.style.cssText = 'position:absolute; left:-10000px; top:0; width:900px; background:#ffffff;';
      document.body.appendChild(container);

      // Wait for images/fonts to load
      await new Promise(resolve => setTimeout(resolve, 500));

      try {
        await generateBrandedPdf({
          element: container,
          filename: `Opportunity_Portfolio_${study?.name?.replace(/\s+/g, '_') || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`,
          // Body already has its own cover + footer, so suppress the helper's defaults.
          skipCover: true,
          skipLastPage: true,
        });
      } finally {
        if (container.parentElement) document.body.removeChild(container);
      }

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF: ' + error.message);
      // Clean up if container was added
      const leftover = document.getElementById('portfolio-pdf-content');
      if (leftover) leftover.remove();
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!portfolioData?.calculations) return;
    
    const headers = [
      'Rank', 'CAPEX Tier', 'SEU Code', 'Equipment', 'Opportunity', 'CAPEX Item',
      'CAPEX Low (INR)', 'CAPEX High (INR)', 'Annual Savings (INR)',
      'Payback (Years)', 'ROI %', 'tCO2e/Year', 'Lifetime tCO2e',
      'Abatement Cost (INR/tCO2e)', 'Priority Score'
    ];
    
    const rows = filteredCalculations.map(c => [
      c.rank, c.capex_tier, c.seu_code, c.equipment_type, c.opportunity_name, c.capex_item_name,
      c.estimated_capex_low, c.estimated_capex_high, c.estimated_annual_savings_inr,
      c.simple_payback_years, c.roi_percent, c.tco2e_reduction_annual, c.tco2e_reduction_lifetime,
      c.abatement_cost, c.priority_score
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `opportunity_portfolio_${study.id}.csv`;
    a.click();
  };

  // Custom MACC tooltip
  const MACCTooltip = ({ active, payload }) => {
    if (active && payload && payload[0]) {
      const data = payload[0].payload;
      const isCostSaving = data.abatementCost < 0;
      return (
        <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg max-w-xs">
          <p className="font-semibold text-gray-900 text-sm mb-1">{data.fullName}</p>
          <div className="space-y-1 text-xs">
            <p className={isCostSaving ? 'text-green-600 font-medium' : 'text-gray-600'}>
              Abatement Cost: {isCostSaving ? '−' : ''}₹{Math.abs(data.abatementCost)?.toLocaleString()}/tCO₂e
              {isCostSaving && ' (Net Saving)'}
            </p>
            <p className="text-gray-600">Lifetime tCO₂e: {data.tco2e?.toLocaleString()}</p>
            <p className="text-gray-600">Payback: {data.payback || 'N/A'} years</p>
            <p className="text-gray-500">Tier: {data.capexTier}</p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Generating portfolio and MACC analysis...</p>
        </div>
      </div>
    );
  }

  if (!portfolioData || !portfolioData.calculations?.length) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">No Calculations Available</h3>
        <p className="text-gray-600 mb-4">
          Complete SEU matching first to generate the opportunity portfolio.
        </p>
        <button onClick={onBack} className="text-indigo-600 hover:underline" data-testid="go-to-match-btn">
          Go to Match & Calculate
        </button>
      </div>
    );
  }

  const summary = portfolioData.summary || {};
  const tierCounts = summary.tier_counts || {};

  return (
    <div className="space-y-4" data-testid="opportunity-portfolio">
      {/* AI Report Modal */}
      {showAiReport && aiReport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6" />
                <h3 className="text-lg font-bold">AI Portfolio Analysis Report</h3>
              </div>
              <button onClick={() => setShowAiReport(false)} className="p-1 hover:bg-white/20 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {/* Handle parse error / raw response */}
              {aiReport.parse_error ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-bold text-amber-900 mb-2">AI Report (Raw Response)</h4>
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{aiReport.raw_response || 'No response received.'}</p>
                </div>
              ) : (<>
              {/* Executive Summary */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" /> Executive Summary
                </h4>
                <p className="text-gray-700 leading-relaxed">{typeof aiReport.executive_summary === 'string' ? aiReport.executive_summary : JSON.stringify(aiReport.executive_summary)}</p>
              </div>

              {/* Key Findings */}
              {Array.isArray(aiReport.key_findings) && aiReport.key_findings.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Key Findings</h4>
                  <ul className="space-y-2">
                    {aiReport.key_findings.map((finding, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-700">
                        <span className="text-indigo-500 mt-1">→</span>
                        {typeof finding === 'string' ? finding : (finding?.name ? `${finding.name}${finding.equipment ? ` (${finding.equipment})` : ''}${finding.savings ? ` — ₹${Number(finding.savings).toLocaleString()} savings` : ''}` : JSON.stringify(finding))}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CAPEX Strategy */}
              {aiReport.capex_strategy && (
                <div className="mb-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">CAPEX Strategy Recommendations</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h5 className="font-semibold text-green-800 mb-2">No CAPEX Quick Wins</h5>
                      <ul className="text-sm text-green-700 space-y-1">
                        {aiReport.capex_strategy.no_capex_quick_wins?.map((item, i) => (
                          <li key={i}>• {typeof item === 'string' ? item : (item?.name || JSON.stringify(item))}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h5 className="font-semibold text-blue-800 mb-2">Low CAPEX Short-Term</h5>
                      <ul className="text-sm text-blue-700 space-y-1">
                        {aiReport.capex_strategy.low_capex_short_term?.map((item, i) => (
                          <li key={i}>• {typeof item === 'string' ? item : (item?.name || JSON.stringify(item))}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h5 className="font-semibold text-amber-800 mb-2">Medium/High CAPEX Long-Term</h5>
                      <ul className="text-sm text-amber-700 space-y-1">
                        {aiReport.capex_strategy.medium_high_capex_long_term?.map((item, i) => (
                          <li key={i}>• {typeof item === 'string' ? item : (item?.name || JSON.stringify(item))}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Implementation Roadmap */}
              {aiReport.implementation_roadmap && (
                <div className="mb-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">Implementation Roadmap</h4>
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-indigo-200"></div>
                    {[
                      { phase: 'Phase 1 (0-6 months)', items: aiReport.implementation_roadmap.phase_1_0_6_months, color: 'bg-green-500' },
                      { phase: 'Phase 2 (6-12 months)', items: aiReport.implementation_roadmap.phase_2_6_12_months, color: 'bg-blue-500' },
                      { phase: 'Phase 3 (12-24 months)', items: aiReport.implementation_roadmap.phase_3_12_24_months, color: 'bg-purple-500' }
                    ].map((phase, idx) => (
                      <div key={idx} className="relative pl-10 pb-6">
                        <div className={`absolute left-2 w-5 h-5 rounded-full ${phase.color} border-4 border-white`}></div>
                        <h5 className="font-semibold text-gray-900 mb-2">{phase.phase}</h5>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {phase.items?.map((item, i) => (
                            <li key={i}>• {typeof item === 'string' ? item : (item?.name || JSON.stringify(item))}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Risk Assessment */}
              {Array.isArray(aiReport.risk_assessment) && aiReport.risk_assessment.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" /> Risk Assessment
                  </h4>
                  <div className="space-y-3">
                    {aiReport.risk_assessment.map((risk, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3">
                        <p className="font-medium text-gray-800">{typeof risk === 'string' ? risk : (risk?.risk || JSON.stringify(risk))}</p>
                        {risk?.mitigation && <p className="text-sm text-gray-600 mt-1"><strong>Mitigation:</strong> {String(risk.mitigation)}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Conclusion */}
              {aiReport.conclusion && (
                <div className="mb-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Conclusion</h4>
                  <p className="text-gray-700">{typeof aiReport.conclusion === 'string' ? aiReport.conclusion : JSON.stringify(aiReport.conclusion)}</p>
                </div>
              )}

              {/* Priority Actions */}
              {Array.isArray(aiReport.priority_actions) && aiReport.priority_actions.length > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <h4 className="font-bold text-indigo-900 mb-2">Top 3 Priority Actions</h4>
                  <ol className="list-decimal list-inside text-indigo-800 space-y-1">
                    {aiReport.priority_actions.map((action, idx) => (
                      <li key={idx}>{typeof action === 'string' ? action : (action?.name || JSON.stringify(action))}</li>
                    ))}
                  </ol>
                </div>
              )}
              </>)}
            </div>
          </div>
        </div>
      )}

      {/* Back Button & Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1 text-sm"
          data-testid="back-to-match-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Match & Calculate
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={generateAiReport}
            disabled={aiReportLoading}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 text-sm"
            data-testid="ai-report-btn"
          >
            {aiReportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            AI Report
          </button>
          <button
            onClick={generatePdfReport}
            disabled={pdfLoading}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 text-sm"
            data-testid="pdf-report-btn"
          >
            {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            PDF Report
          </button>
          <button
            onClick={downloadCSV}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" /> CSV
          </button>
          <button
            onClick={fetchPortfolioData}
            className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-3">
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <Award className="w-5 h-5 opacity-80" />
            <span className="text-sm opacity-80">Opportunities</span>
          </div>
          <p className="text-2xl font-bold">{portfolioData.total_opportunities}</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-5 h-5 opacity-80" />
            <span className="text-sm opacity-80">Total CAPEX</span>
          </div>
          <p className="text-lg font-bold">
            ₹{(summary.total_capex_low / 100000)?.toFixed(1)}L - {(summary.total_capex_high / 100000)?.toFixed(1)}L
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-5 h-5 opacity-80" />
            <span className="text-sm opacity-80">Annual Savings</span>
          </div>
          <p className="text-lg font-bold">₹{(summary.total_annual_savings / 100000)?.toFixed(1)}L</p>
        </div>
        
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <Leaf className="w-5 h-5 opacity-80" />
            <span className="text-sm opacity-80">tCO₂e/Year</span>
          </div>
          <p className="text-2xl font-bold">{summary.total_tco2e_annual?.toLocaleString()}</p>
        </div>
        
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-5 h-5 opacity-80" />
            <span className="text-sm opacity-80">Avg Payback</span>
          </div>
          <p className="text-2xl font-bold">{summary.avg_payback_years?.toFixed(1) || '-'} yr</p>
        </div>
      </div>

      {/* CAPEX Tier Filter */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <span className="font-semibold text-gray-900">Filter by CAPEX Tier</span>
          </div>
          <span className="text-sm text-gray-500">
            Showing {filteredCalculations.length} of {portfolioData.total_opportunities} opportunities
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveCapexFilter('All')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              activeCapexFilter === 'All'
                ? 'bg-gray-800 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            data-testid="filter-all"
          >
            All ({portfolioData.total_opportunities})
          </button>
          {Object.entries(CAPEX_TIERS).map(([tier, config]) => (
            <button
              key={tier}
              onClick={() => setActiveCapexFilter(tier)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                activeCapexFilter === tier
                  ? `${config.bgColor} ${config.textColor} ${config.borderColor} border-2`
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-transparent'
              }`}
              data-testid={`filter-${tier.toLowerCase().replace(' ', '-')}`}
            >
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }}></span>
              {tier} ({tierCounts[tier] || 0})
            </button>
          ))}
        </div>
      </div>

      {/* MACC Curve */}
      <div className="bg-white rounded-xl border border-gray-200 p-4" ref={chartRef}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-gray-900">Marginal Abatement Cost Curve (MACC)</h3>
            <p className="text-sm text-gray-500">Ordered by cost-effectiveness (most cost-effective on left)</p>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span className="text-gray-600">Below line = Net Saving</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-400 rounded"></div>
              <span className="text-gray-600">Above line = Net Cost</span>
            </div>
          </div>
        </div>
        
        {maccData.length > 0 ? (
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={maccData} 
                margin={{ top: 20, right: 30, left: 60, bottom: 80 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="cumulativeWidth"
                  type="number"
                  domain={[0, 'dataMax']}
                  tickFormatter={(v) => `${Math.round(v)}`}
                  label={{ value: 'Cumulative tCO₂e Reduction (Lifetime)', position: 'bottom', offset: 50, style: { fontSize: 12 } }}
                />
                <YAxis 
                  tickFormatter={(v) => `${v >= 0 ? '' : '−'}₹${Math.abs(v).toLocaleString()}`}
                  label={{ value: 'Abatement Cost (₹/tCO₂e)', angle: -90, position: 'insideLeft', offset: -45, style: { fontSize: 12 } }}
                />
                <Tooltip content={<MACCTooltip />} />
                <ReferenceLine y={0} stroke="#000" strokeWidth={2} />
                <Bar 
                  dataKey="abatementCost" 
                  name="Abatement Cost"
                  maxBarSize={60}
                >
                  {maccData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.tierColor}
                      stroke={entry.abatementCost < 0 ? '#16a34a' : '#dc2626'}
                      strokeWidth={entry.abatementCost < 0 ? 2 : 0}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-48 flex items-center justify-center text-gray-500">
            No data available for MACC curve with current filter
          </div>
        )}
        
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <strong>Reading the MACC:</strong> Each bar represents an opportunity. 
              <strong> Height</strong> = cost per tCO₂e reduced (negative = net saving after payback). 
              <strong> Width</strong> = tCO₂e reduction potential. 
              Bars <strong>below</strong> the zero line are cost-effective (savings exceed CAPEX over lifecycle).
            </div>
          </div>
        </div>
      </div>

      {/* CAPEX Tier Summary Cards */}
      <div className="grid grid-cols-4 gap-3">
        {Object.entries(CAPEX_TIERS).map(([tier, config]) => {
          const tierOpps = portfolioData.calculations.filter(c => c.capex_tier === tier);
          const tierSavings = tierOpps.reduce((s, c) => s + (c.estimated_annual_savings_inr || 0), 0);
          const tierTco2e = tierOpps.reduce((s, c) => s + (c.tco2e_reduction_annual || 0), 0);
          
          return (
            <div key={tier} className={`${config.bgColor} ${config.borderColor} border rounded-xl p-4`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }}></span>
                <span className={`font-semibold ${config.textColor}`}>{tier}</span>
              </div>
              <div className="space-y-1 text-sm">
                <p className="text-gray-600">{tierOpps.length} opportunities</p>
                <p className="text-gray-600">₹{(tierSavings / 100000).toFixed(1)}L savings/yr</p>
                <p className="text-gray-600">{tierTco2e.toFixed(1)} tCO₂e/yr</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Ranked Opportunities Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h4 className="font-semibold text-gray-900">Ranked Opportunities</h4>
          <span className="text-sm text-gray-500">Sorted by Priority Score</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="opportunities-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Rank</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">CAPEX Tier</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Equipment / SEU</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Opportunity</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-600">CAPEX (INR)</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-600">Savings/yr</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-600">Payback</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-600">tCO₂e/yr</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-600">₹/tCO₂e</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-600">Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredCalculations.map((calc, idx) => {
                const tierConfig = CAPEX_TIERS[calc.capex_tier] || {};
                const isCostSaving = calc.abatement_cost < 0;
                
                return (
                  <tr key={idx} className={`hover:bg-gray-50 ${idx < 3 ? 'bg-green-50/50' : ''}`}>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                        idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                        idx === 1 ? 'bg-gray-300 text-gray-700' :
                        idx === 2 ? 'bg-orange-300 text-orange-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {calc.rank}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${tierConfig.bgColor} ${tierConfig.textColor}`}>
                        {calc.capex_tier}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{calc.equipment_name || calc.equipment_type}</div>
                      <div className="text-xs text-gray-500">{calc.seu_code}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-800">{calc.capex_item_name}</div>
                      <div className="text-xs text-gray-500">{calc.opportunity_name}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="font-medium">{(calc.estimated_capex_low / 1000)?.toFixed(0)}K - {(calc.estimated_capex_high / 1000)?.toFixed(0)}K</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="font-medium text-green-600">₹{(calc.estimated_annual_savings_inr / 100000)?.toFixed(2)}L</div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {calc.simple_payback_years ? (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          calc.simple_payback_years < 2 ? 'bg-green-100 text-green-700' :
                          calc.simple_payback_years < 4 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {calc.simple_payback_years} yr
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="text-emerald-600 font-medium">{calc.tco2e_reduction_annual?.toFixed(1)}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className={`font-medium ${isCostSaving ? 'text-green-600' : 'text-orange-600'}`}>
                        {isCostSaving ? '−' : ''}₹{Math.abs(calc.abatement_cost)?.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        calc.priority_score > 1 ? 'bg-green-600 text-white' :
                        calc.priority_score > 0.5 ? 'bg-yellow-500 text-white' :
                        'bg-gray-400 text-white'
                      }`}>
                        {calc.priority_score?.toFixed(2)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 bg-gray-50 text-center text-sm text-gray-500">
            Showing {filteredCalculations.length} of {portfolioData?.total_opportunities || filteredCalculations.length} opportunities.
          </div>
      </div>
    </div>
  );
};

export default OpportunityPortfolio;
